import { Text } from '../Text/Text';
import styles from './ProgressBar.module.css';

/**
 * Figma: `Progress Bar` (334:18792) — "Segmented bar visualizing completion
 * from 0–100%. The multi-color segmented style conveys both amount and quality
 * — low progress reads as red/warning, mid as amber, high as green/healthy."
 *
 * Figma models progress as 11 discrete variants (0%–100% in 10% steps) because
 * a variant property cannot hold a continuous value; the annotation says to
 * "select the nearest 10% step variant to represent the current value". That
 * makes the steps an authoring limitation, not the model, so this takes a
 * number and derives the fill — every value in between renders correctly.
 */

/** Figma draws 30 rectangles; the count is structural, not a token. */
const SEGMENT_COUNT = 30;

/**
 * Colour is decided by a segment's *position* in the bar, not by the current
 * value — so the filled run reads red → amber → green as it grows. Boundaries
 * are the ones the annotation states and 18 of the 22 variants bind.
 */
const NEGATIVE_THROUGH = 5; // segments 1–5
const NEUTRAL_THROUGH = 15; // segments 6–15, then positive to 30

const toneClass = (index: number): string => {
  if (index < NEGATIVE_THROUGH) return styles.negative;
  if (index < NEUTRAL_THROUGH) return styles.neutral;
  return styles.positive;
};

/**
 * Figma `Type`. `Default` is the red → amber → green run above. `AI Credit`
 * (added 2026-09-22) paints every filled segment one colour,
 * `component/progress/ai-surface`, and leaves the empty ones on
 * `component/progress/surface` as before.
 *
 * The distinction is what the bar means, not how it looks: a credit balance has
 * no healthy end, so colouring the run by position would say a nearly empty
 * allowance is "bad" and a full one "good". The file draws it only at
 * `Size=lg, Label?=False`, which is the `AI credits` card's bar.
 */
export type ProgressBarType = 'default' | 'aiCredit';

export type ProgressBarProps = {
  /** Completion, 0–100. Values outside the range are clamped. */
  value: number;
  /**
   * Figma `Size`. `lg` is 256 wide with 4px segments and a `Text sm` label; `sm`
   * has 2px segments and a `Text xs` label, and is 128 wide bare or 144 with the
   * label — the only variant whose frame grows to make room for it. Same 30
   * segments and same 16px height either way. `Table cell` `Type=Progress bar`
   * embeds the labelled `sm`, which is the only place the design uses it.
   */
  size?: 'lg' | 'sm';
  /** Figma `Type` — see `ProgressBarType`. */
  type?: ProgressBarType;
  /** Figma `Label?` — renders the trailing percentage. */
  showLabel?: boolean;
  /**
   * Accessible name for the bar. Not in Figma: a progressbar with no name is
   * announced as an unlabelled value, so this defaults rather than being absent.
   */
  label?: string;
  className?: string;
};

export function ProgressBar({
  value,
  size = 'lg',
  type = 'default',
  showLabel = false,
  label = 'Progress',
  className,
}: ProgressBarProps) {
  const clamped = Math.min(100, Math.max(0, value));
  const percent = Math.round(clamped);
  const filled = Math.round((clamped / 100) * SEGMENT_COUNT);

  return (
    <div
      className={[
        styles.root,
        size === 'sm' && styles.sm,
        size === 'sm' && showLabel && styles.smLabelled,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      /* Not in Figma — Figma describes appearance, not semantics. */
      role="progressbar"
      aria-label={label}
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {Array.from({ length: SEGMENT_COUNT }, (_, i) => (
        <div
          key={i}
          className={`${styles.segment} ${
            i < filled ? (type === 'aiCredit' ? styles.ai : toneClass(i)) : styles.empty
          }`}
          aria-hidden="true"
        />
      ))}
      {showLabel && (
        /*
         * Figma `Spacer` — an unfilled rectangle the width of one segment, sitting
         * between the last segment and the label. Under `space-between` it buys the
         * label a second gap plus a segment's width of clearance instead of the
         * single gap it had, which is what stops the percentage reading as part of
         * the bar. Present only on the labelled variants, which is why it is inside
         * this branch rather than beside the segment loop.
         */
        <div className={`${styles.segment} ${styles.spacer}`} aria-hidden="true" />
      )}
      {showLabel && (
        /* Redundant with aria-valuenow, so hidden from the accessibility tree. */
        <span className={styles.label} aria-hidden="true">
          {/* Figma sets the label a step down at `sm` — `Text xs` (12/12)
              against `lg`'s `Text sm` (14/14). */}
          <Text size={size === 'sm' ? 'xs' : 'sm'} leading="none" weight="regular" color="primary">
            {percent}%
          </Text>
        </span>
      )}
    </div>
  );
}
